import { useEffect, lazy, Suspense, useMemo, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';

import { usePageTracking } from './hooks/usePageTracking';
import { ThemeProvider, CssBaseline, Box, CircularProgress, useMediaQuery } from '@mui/material';
import { createOperatorTheme } from './theme/operatorTheme';
import './theme/fonts';
import { useAuthStore } from './stores/authStore';
import { useCompanyStore } from './stores/companyStore';
import { useThemeStore } from './stores/themeStore';
import { ErrorBoundary } from './components/ErrorBoundary';
// Side-effect import: augments MUI's Theme type with `.operator`
import './theme/operatorTheme';
import { ToastProvider } from './lib/toast';
import { PWAPrompt } from './components/PWAPrompt';
import {
  attemptChunkRecoveryReload,
  clearChunkRecoveryReloadFlag,
  isChunkLoadError,
} from './lib/chunk-recovery';
const CommandPaletteProvider = lazy(() =>
  import('./components/CommandPalette/CommandPalette').then((module) => ({
    default: module.CommandPaletteProvider,
  })),
);
const LoginPage = lazy(() => import('./pages/LoginPage'));
const MfaChallengePage = lazy(() => import('./pages/MfaChallengePage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PlansPage   = lazy(() => import('./pages/PlansPage'));
const DemoPage    = lazy(() => import('./pages/DemoPage'));
const ComplianceSalesPackPage = lazy(() => import('./pages/ComplianceSalesPackPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TanksPage = lazy(() => import('./pages/TanksPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const ForecastPage = lazy(() => import('./pages/ForecastPage'));
const TankVisualizationPage = lazy(() => import('./pages/TankVisualizationPage'));
const DispatchPage = lazy(() => import('./pages/DispatchPage'));
const OpsMapPage   = lazy(() => import('./pages/OpsMapPage'));
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'));
const OnboardingFlow = lazy(() => import('./pages/Onboarding/OnboardingFlow'));
const OnboardingV2 = lazy(() => import('./pages/Onboarding/v2/OnboardingV2'));
// Feature flag: when true, `/onboarding` renders the new v2 experience.
// `/onboarding-v2` always renders v2 for previewing without flipping the flag.
const USE_ONBOARDING_V2 = true;
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const MobileOnlyPage = lazy(() => import('./pages/MobileOnlyPage'));
const OperatorMiningShiftPage = lazy(() => import('./pages/Operator/OperatorMiningShiftPage'));
const OperatorMiningFillPage = lazy(() => import('./pages/Operator/OperatorMiningFillPage'));
const OperatorMiningHistoryPage = lazy(() => import('./pages/Operator/OperatorMiningHistoryPage'));
const OperatorMiningSessionPage = lazy(() => import('./pages/Operator/OperatorMiningSessionPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const OutreachTodayPage = lazy(() => import('./pages/Admin/outreach/OutreachTodayPage'));
const OutreachLeadDetailPage = lazy(() => import('./pages/Admin/outreach/OutreachLeadDetailPage'));
const OutreachAllLeadsPage = lazy(() => import('./pages/Admin/outreach/OutreachAllLeadsPage'));
const OutreachInsightsPage = lazy(() => import('./pages/Admin/outreach/OutreachInsightsPage'));
const OutreachHistoryPage = lazy(() => import('./pages/Admin/outreach/OutreachHistoryPage'));
const OutreachSentPage = lazy(() => import('./pages/Admin/outreach/OutreachSentPage'));
const OutreachSendPlanPage = lazy(() => import('./pages/Admin/outreach/OutreachSendPlanPage'));
const OperatorShowcasePage = lazy(() => import('./pages/dev/OperatorShowcasePage'));
const LayoutWrapper = lazy(() => import('./components/Layout/LayoutWrapper'));
const SectorRouter = lazy(() => import('./pages/SectorRouter'));
// Mining-specific pages
const MiningFleetPage = lazy(() => import('./pages/Mining/MiningFleetPage'));
const MiningFuelLogPage = lazy(() => import('./pages/Mining/MiningFuelLogPage'));
const FuelOperationsPage = lazy(() => import('./pages/Mining/FuelOperationsPage'));
const MiningForecastPage = lazy(() => import('./pages/Mining/MiningForecastPage'));
const MiningTeamPage = lazy(() => import('./pages/Mining/MiningTeamPage'));
const MiningReconciliationPage = lazy(() => import('./pages/Mining/MiningReconciliationPage'));
const MiningSitesPage = lazy(() => import('./pages/Mining/MiningSitesPage'));
const MiningTrashPage = lazy(() => import('./pages/Mining/MiningTrashPage'));
const MiningShiftReportPage = lazy(() => import('./pages/Mining/MiningShiftReportPage'));
const MiningEquipmentPage = lazy(() => import('./pages/Mining/MiningEquipmentPage'));
const MiningPurchasesPage = lazy(() => import('./pages/Mining/MiningPurchasesPage'));
const TelemetryCommandCenter = lazy(() => import('./pages/Mining/TelemetryCommandCenter'));
// Logistics-specific pages
const FleetControlDashboard  = lazy(() => import('./pages/Logistics/FleetControlDashboard'));
const FleetVehiclesPage      = lazy(() => import('./pages/Logistics/FleetVehiclesPage'));
const FleetDriversPage       = lazy(() => import('./pages/Logistics/FleetDriversPage'));
const FleetFillLogPage       = lazy(() => import('./pages/Logistics/FleetFillLogPage'));
const FleetFraudAlertsPage   = lazy(() => import('./pages/Logistics/FleetFraudAlertsPage'));
const FleetAnalyticsPage     = lazy(() => import('./pages/Logistics/FleetAnalyticsPage'));
const FleetLiveMapPage       = lazy(() => import('./pages/Logistics/FleetLiveMapPage'));
const FleetTripsPage         = lazy(() => import('./pages/Logistics/FleetTripsPage'));
const Fleet3DPage            = lazy(() => import('./pages/Logistics/Fleet3DPage'));
// Agriculture-specific pages
const FarmDashboard          = lazy(() => import('./pages/Agriculture/FarmDashboard'));
const FarmFieldsPage         = lazy(() => import('./pages/Agriculture/FarmFieldsPage'));
const FarmEquipmentPage      = lazy(() => import('./pages/Agriculture/FarmEquipmentPage'));
const FarmBowsersPage        = lazy(() => import('./pages/Agriculture/FarmBowsersPage'));
const FarmFuelLogPage        = lazy(() => import('./pages/Agriculture/FarmFuelLogPage'));
const FarmAnalyticsPage      = lazy(() => import('./pages/Agriculture/FarmAnalyticsPage'));
const FarmForecastPage       = lazy(() => import('./pages/Agriculture/FarmForecastPage'));
// Construction-specific pages
const JobSiteManager         = lazy(() => import('./pages/Construction/JobSiteManager'));
const SiteJobsPage           = lazy(() => import('./pages/Construction/SiteJobsPage'));
const SkidTanksPage          = lazy(() => import('./pages/Construction/SkidTanksPage'));
const RfidTagsPage           = lazy(() => import('./pages/Construction/RfidTagsPage'));
const SiteFuelLogPage        = lazy(() => import('./pages/Construction/SiteFuelLogPage'));
const SiteAnalyticsPage      = lazy(() => import('./pages/Construction/SiteAnalyticsPage'));
const SiteForecastPage       = lazy(() => import('./pages/Construction/SiteForecastPage'));
const SiteVatTrackerPage     = lazy(() => import('./pages/Construction/SiteVatTrackerPage'));
// SARS compliance pages
const MiningSettingsPage        = lazy(() => import('./pages/Mining/MiningSettingsPage'));
const MiningSarsRebatePage      = lazy(() => import('./pages/Mining/MiningSarsRebatePage'));
const MiningAuditSimulatorPage  = lazy(() => import('./pages/Mining/MiningAuditSimulatorPage'));
const MiningProvenancePage      = lazy(() => import('./pages/Mining/MiningProvenancePage'));
const MiningCarbonTaxPage       = lazy(() => import('./pages/Mining/MiningCarbonTaxPage'));
const MiningAdvisorsPage        = lazy(() => import('./pages/Mining/MiningAdvisorsPage'));
const MiningBillingPage         = lazy(() => import('./pages/Mining/MiningBillingPage'));
const SARSHubPage               = lazy(() => import('./pages/Mining/SARSHubPage'));
const MiningRfidPage            = lazy(() => import('./pages/Mining/MiningRfidPage'));
const MiningAuditTrailPage      = lazy(() => import('./pages/Mining/MiningAuditTrailPage'));
const MiningOpsHealthPage        = lazy(() => import('./pages/Mining/MiningOpsHealthPage'));
const FleetSarsReimbursementPage = lazy(() => import('./pages/Logistics/FleetSarsReimbursementPage'));
const FarmSarsRefundPage        = lazy(() => import('./pages/Agriculture/FarmSarsRefundPage'));
// Power Generation sector
const PowerGenDashboard         = lazy(() => import('./pages/Power/PowerGenDashboard'));
const GensetFleetPage           = lazy(() => import('./pages/Power/GensetFleetPage'));
const PowerFuelLogPage          = lazy(() => import('./pages/Power/PowerFuelLogPage'));
const PowerRuntimePage          = lazy(() => import('./pages/Power/PowerRuntimePage'));
const PowerAnalyticsPage        = lazy(() => import('./pages/Power/PowerAnalyticsPage'));
const PowerForecastPage         = lazy(() => import('./pages/Power/PowerForecastPage'));
const PowerSarsRebatePage       = lazy(() => import('./pages/Power/PowerSarsRebatePage'));
const CompliancePage            = lazy(() => import('./pages/CompliancePage'));
const SarsBinderPage            = lazy(() => import('./pages/SarsBinderPage'));
const InvoicePrintPage          = lazy(() => import('./pages/InvoicePrintPage'));
// Axion AI pages
const AxionAlertsPage            = lazy(() => import('./pages/Axion/AxionAlertsPage'));
// Billing pages
const CheckoutPage              = lazy(() => import('./pages/CheckoutPage'));
const BillingCallbackPage       = lazy(() => import('./pages/BillingCallbackPage'));
const BillingSettingsPage       = lazy(() => import('./pages/BillingSettingsPage'));
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { UserRole } from './stores/authStore';
import {
  FOUNDER_ADMIN_EMAILS,
  OBSERVABILITY_ADMIN_EMAILS,
  shouldUseTenantWorkspace,
} from './lib/founder-access';

const API_BASE_URL = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3000'}/api`;

type TenantRealtimeResource =
  | 'tanks'
  | 'transactions'
  | 'dispatch'
  | 'fuelPrices'
  | 'dashboardStats'
  | 'axionAlerts';

type TenantRealtimeEvent = {
  tenantId: string;
  resources: TenantRealtimeResource[];
  at: string;
  reason?: string;
};

const refreshRealtimeResources = async (resources: TenantRealtimeResource[]) => {
  const resourceSet = new Set(resources);
  const refreshes: Array<Promise<unknown>> = [];

  if (resourceSet.has('tanks') || resourceSet.has('dashboardStats')) {
    refreshes.push(
      import('./stores/tanksStore').then(({ useTanksStore }) => Promise.all([
        resourceSet.has('tanks')
          ? useTanksStore.getState().fetchTanks({ force: true })
          : Promise.resolve(),
        resourceSet.has('dashboardStats')
          ? useTanksStore.getState().fetchStats({ force: true })
          : Promise.resolve(),
      ])),
    );
  }

  if (resourceSet.has('transactions')) {
    refreshes.push(
      import('./stores/transactionsStore').then(({ useTransactionsStore }) =>
        useTransactionsStore.getState().fetchTransactions({ force: true }),
      ),
    );
  }

  if (resourceSet.has('dispatch')) {
    refreshes.push(
      import('./stores/dispatchStore').then(({ useDispatchStore }) =>
        useDispatchStore.getState().fetchDeliveries({ force: true }),
      ),
    );
  }

  if (resourceSet.has('fuelPrices')) {
    refreshes.push(
      import('./stores/fuelPricesStore').then(({ useFuelPricesStore }) =>
        useFuelPricesStore.getState().fetchPrices({ force: true }),
      ),
    );
  }

  if (resourceSet.has('axionAlerts')) {
    refreshes.push(
      import('./stores/axionStore').then(({ useAxionStore }) =>
        useAxionStore.getState().fetchAlertCount(),
      ),
    );
  }

  await Promise.allSettled(refreshes);
};

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
      <CircularProgress size={28} />
    </Box>
  );
}


/** Inner component renders inside Router, so hooks that depend on location work here */
function AppRoutes() {
  usePageTracking();
  const location = useLocation();




  return (
    <>
      <SpeedInsights route={location.pathname} />
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/mfa" element={<MfaChallengePage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/invite/accept" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/dev/operator"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OperatorShowcasePage />
              </ProtectedRoute>
            }
          />

          {/* Onboarding — protected, full-page (no sidebar) */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                {USE_ONBOARDING_V2 ? <OnboardingV2 /> : <OnboardingFlow />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding-v2"
            element={
              <ProtectedRoute>
                <OnboardingV2 />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding-legacy"
            element={
              <ProtectedRoute>
                <OnboardingFlow />
              </ProtectedRoute>
            }
          />

          {/* Admin command center — restricted to founder emails only.
              `/founder` is kept as a legacy alias that redirects to `/admin`. */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <LayoutWrapper>
                  <AdminDashboard />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachTodayPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/lead/:leadId"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachLeadDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/all"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachAllLeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/insights"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachInsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/history"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/sent"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachSentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/plan"
            element={
              <ProtectedRoute allowedEmails={FOUNDER_ADMIN_EMAILS}>
                <OutreachSendPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outreach/legacy"
            element={<Navigate to="/admin/outreach" replace />}
          />
          <Route path="/founder" element={<Navigate to="/admin" replace />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DashboardPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tanks"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <TanksPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <TransactionsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}>
                <LayoutWrapper>
                  <UsersPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <ProfilePage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}>
                <LayoutWrapper>
                  <AnalyticsPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}>
                <LayoutWrapper>
                  <PricingPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/forecast"
            element={
              <ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}>
                <LayoutWrapper>
                  <ForecastPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dispatch"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <DispatchPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ops-map"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <OpsMapPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/observability"
            element={
              <ProtectedRoute allowedEmails={OBSERVABILITY_ADMIN_EMAILS}>
                <LayoutWrapper>
                  <ObservabilityPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/3d"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <TankVisualizationPage />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* ── Mining sector routes ──────────────────────────────────────── */}
          {/* Must be listed BEFORE the /sector/:sectorKey wildcard at the bottom. */}
          {/*                                                                     */}
          {/* Role model (operator workflow has a dedicated /operator/mining web shell): */}
          {/*   OWNER / DIRECTOR         → full business/admin access             */}
          {/*   MANAGER                  → operational + assigned-site admin      */}
          {/*   VIEWER                     → read-only everywhere except Team,    */}
          {/*                                 RFID, and Fueling Equipment admin   */}
          {/* Command Center — explicit route so it doesn't fall through to the   */}
          {/* unguarded /sector/:sectorKey wildcard at the bottom of the tree.    */}
          <Route path="/sector/mining"          element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><TelemetryCommandCenter /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/sites"    element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningSitesPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/trash"    element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR]}><LayoutWrapper><MiningTrashPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/fuel-ops" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><FuelOperationsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/shifts"   element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningShiftReportPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Legacy routes — keep for bookmarks/links */}
          <Route path="/sector/mining/tanks"    element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><FuelOperationsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/fuellog"  element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningFuelLogPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Fleet — VIEWER can see (finance/audit needs fleet visibility) */}
          <Route path="/sector/mining/fleet"     element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningFleetPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Fueling Equipment — hardware registry, management only (no VIEWER) */}
          <Route path="/sector/mining/equipment" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><MiningEquipmentPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Finance & Compliance — OWNER / DIRECTOR / MANAGER / VIEWER */}
          <Route path="/sector/mining/forecast"       element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningForecastPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/reconciliation" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningReconciliationPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Settings — write-only admin surfaces (no VIEWER) */}
          <Route path="/sector/mining/team"      element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><MiningTeamPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/purchases" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningPurchasesPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/rfid"      element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><MiningRfidPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Audit Trail — owner / director / manager (tenant-wide immutable history) */}
          <Route path="/sector/mining/audit"     element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><MiningAuditTrailPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/ops-health" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR]}><LayoutWrapper><MiningOpsHealthPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Logistics sector routes ──────────────────────────────────── */}
          <Route path="/sector/fleet"          element={<ProtectedRoute><LayoutWrapper><FleetControlDashboard /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/vehicles" element={<ProtectedRoute><LayoutWrapper><FleetVehiclesPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/drivers"  element={<ProtectedRoute><LayoutWrapper><FleetDriversPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/fills"    element={<ProtectedRoute><LayoutWrapper><FleetFillLogPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/fraud"     element={<ProtectedRoute><LayoutWrapper><FleetFraudAlertsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/analytics" element={<ProtectedRoute><LayoutWrapper><FleetAnalyticsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/map"       element={<ProtectedRoute><LayoutWrapper><FleetLiveMapPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/trips"     element={<ProtectedRoute><LayoutWrapper><FleetTripsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/3d"        element={<ProtectedRoute><LayoutWrapper><Fleet3DPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Agriculture sector routes ─────────────────────────────────── */}
          <Route path="/sector/farm"           element={<ProtectedRoute><LayoutWrapper><FarmDashboard /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/fields"    element={<ProtectedRoute><LayoutWrapper><FarmFieldsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/equipment" element={<ProtectedRoute><LayoutWrapper><FarmEquipmentPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/bowsers"   element={<ProtectedRoute><LayoutWrapper><FarmBowsersPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/fuellog"   element={<ProtectedRoute><LayoutWrapper><FarmFuelLogPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/analytics" element={<ProtectedRoute><LayoutWrapper><FarmAnalyticsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/forecast"  element={<ProtectedRoute><LayoutWrapper><FarmForecastPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Construction sector routes ────────────────────────────────── */}
          <Route path="/sector/jobsite"            element={<ProtectedRoute><LayoutWrapper><JobSiteManager /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/sites"      element={<ProtectedRoute><LayoutWrapper><SiteJobsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/tanks"      element={<ProtectedRoute><LayoutWrapper><SkidTanksPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/rfid"       element={<ProtectedRoute><LayoutWrapper><RfidTagsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/fuellog"    element={<ProtectedRoute><LayoutWrapper><SiteFuelLogPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/analytics"  element={<ProtectedRoute><LayoutWrapper><SiteAnalyticsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/forecast"   element={<ProtectedRoute><LayoutWrapper><SiteForecastPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/jobsite/sars"       element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><SiteVatTrackerPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── SARS sector compliance routes ─────────────────────────────── */}
          <Route path="/sector/mining/settings"     element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><MiningSettingsPage /></LayoutWrapper></ProtectedRoute>} />
          {/* SARS Hub — consolidated single page for DA 66 / reconciliation / forecast / purchases */}
          <Route path="/sector/mining/sars-hub"    element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><SARSHubPage /></LayoutWrapper></ProtectedRoute>} />
          {/* Legacy standalone — kept for bookmarks and deep-links */}
          <Route path="/sector/mining/sars"        element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningSarsRebatePage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/provenance"  element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningProvenancePage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/audit-simulator" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningAuditSimulatorPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/da66"        element={<Navigate to="/sector/mining/sars" replace />} />
          <Route path="/sector/mining/carbon-tax" element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningCarbonTaxPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/advisors"   element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER, UserRole.VIEWER]}><LayoutWrapper><MiningAdvisorsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/mining/billing"    element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR]}><LayoutWrapper><MiningBillingPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/fleet/sars"         element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><FleetSarsReimbursementPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/farm/sars"          element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><FarmSarsRefundPage /></LayoutWrapper></ProtectedRoute>} />
          {/* ── Power Generation sector ─────────────────────────────────────── */}
          <Route path="/sector/power"              element={<ProtectedRoute><LayoutWrapper><PowerGenDashboard /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/gensets"      element={<ProtectedRoute><LayoutWrapper><GensetFleetPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/fuellog"      element={<ProtectedRoute><LayoutWrapper><PowerFuelLogPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/runtime"      element={<ProtectedRoute><LayoutWrapper><PowerRuntimePage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/analytics"    element={<ProtectedRoute><LayoutWrapper><PowerAnalyticsPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/forecast"     element={<ProtectedRoute><LayoutWrapper><PowerForecastPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/sector/power/sars"         element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><PowerSarsRebatePage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/compliance"                element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><CompliancePage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/compliance/binder"        element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.MANAGER]}><LayoutWrapper><SarsBinderPage /></LayoutWrapper></ProtectedRoute>} />
          <Route path="/invoice/:transactionId"   element={<ProtectedRoute><LayoutWrapper><InvoicePrintPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Billing routes ──────────────────────────────────────────────── */}
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/billing/callback" element={<ProtectedRoute><BillingCallbackPage /></ProtectedRoute>} />
          <Route path="/settings/billing" element={<ProtectedRoute><LayoutWrapper><BillingSettingsPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Axion AI routes ─────────────────────────────────────────────── */}
          <Route path="/axion/alerts"              element={<ProtectedRoute roles={[UserRole.OWNER, UserRole.DIRECTOR, UserRole.MANAGER]}><LayoutWrapper><AxionAlertsPage /></LayoutWrapper></ProtectedRoute>} />

          {/* ── Mobile-only role redirect ───────────────────────────────────── */}
          {/* OPERATOR now has a dedicated web shell. DRIVER remains mobile-only. */}
          <Route
            path="/operator/mining"
            element={<ProtectedRoute roles={[UserRole.OPERATOR]}><Navigate to="/operator/mining/shift" replace /></ProtectedRoute>}
          />
          <Route
            path="/operator/mining/shift"
            element={<ProtectedRoute roles={[UserRole.OPERATOR]}><OperatorMiningShiftPage /></ProtectedRoute>}
          />
          <Route
            path="/operator/mining/fill"
            element={<ProtectedRoute roles={[UserRole.OPERATOR]}><OperatorMiningFillPage /></ProtectedRoute>}
          />
          <Route
            path="/operator/mining/history"
            element={<ProtectedRoute roles={[UserRole.OPERATOR]}><OperatorMiningHistoryPage /></ProtectedRoute>}
          />
          <Route
            path="/operator/mining/session"
            element={<ProtectedRoute roles={[UserRole.OPERATOR]}><OperatorMiningSessionPage /></ProtectedRoute>}
          />

          <Route
            path="/mobile-only"
            element={
              <ProtectedRoute>
                <MobileOnlyPage />
              </ProtectedRoute>
            }
          />

          {/* Sector-specific catch-all dashboard fallback (e.g. /sector/mining, /sector/logistics) */}
          <Route
            path="/sector/:sectorKey"
            element={
              <ProtectedRoute>
                <LayoutWrapper>
                  <SectorRouter />
                </LayoutWrapper>
              </ProtectedRoute>
            }
          />

          {/* Default redirect / Landing Page */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/sales-pack" element={<ComplianceSalesPackPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
}

function App() {
  const { loadFromStorage, isAuthenticated, token, user } = useAuthStore();
  const { loadCompany } = useCompanyStore();
  const themeMode = useThemeStore((state) => state.mode);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const computedMode = themeMode === 'system' ? (prefersDarkMode ? 'dark' : 'light') : themeMode;
  const theme = useMemo(() => createOperatorTheme(computedMode), [computedMode]);
  const pendingResourceSetRef = useRef<Set<TenantRealtimeResource>>(new Set());
  const flushTimerRef = useRef<number | null>(null);

  useEffect(() => {
    loadFromStorage();
  }, [loadFromStorage]);

  useEffect(() => {
    const clearReloadFlag = clearChunkRecoveryReloadFlag();

    return () => {
      if (clearReloadFlag !== undefined) {
        window.clearTimeout(clearReloadFlag);
      }
    };
  }, []);

  useEffect(() => {
    const handleError = (event: Event) => {
      const chunkError = event as ErrorEvent;
      if (!isChunkLoadError(chunkError.error ?? chunkError.message)) {
        return;
      }

      if (attemptChunkRecoveryReload()) {
        chunkError.preventDefault?.();
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) {
        return;
      }

      if (attemptChunkRecoveryReload()) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Fetch company data when authenticated (title + favicon handled by companyStore)
  useEffect(() => {
    if (isAuthenticated && shouldUseTenantWorkspace(user)) {
      loadCompany();
    }
  }, [isAuthenticated, loadCompany, user]);

  useEffect(() => {
    const tenantId = user?.tenantId;
    if (!isAuthenticated || !token || !tenantId || !shouldUseTenantWorkspace(user)) {
      return;
    }
    const pendingResources = pendingResourceSetRef.current;

    const flushRefreshes = () => {
      const resources = [...pendingResources];
      pendingResources.clear();
      flushTimerRef.current = null;
      if (resources.length === 0) {
        return;
      }

      void refreshRealtimeResources(resources);
    };

    const streamUrl = `${API_BASE_URL}/realtime/stream?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(streamUrl);

    eventSource.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data) as TenantRealtimeEvent;
        if (!payload || payload.tenantId !== tenantId || !Array.isArray(payload.resources)) {
          return;
        }

        for (const resource of payload.resources) {
          pendingResources.add(resource);
        }
        if (flushTimerRef.current === null) {
          flushTimerRef.current = window.setTimeout(flushRefreshes, 180);
        }
      } catch {
        // Ignore malformed events and keep stream alive
      }
    };

    return () => {
      eventSource.close();
      if (flushTimerRef.current !== null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      pendingResources.clear();
    };
  }, [isAuthenticated, token, user]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {isAuthenticated ? (
          <Suspense fallback={<AppRoutes />}>
            <CommandPaletteProvider>
              <PWAPrompt />
              <AppRoutes />
            </CommandPaletteProvider>
          </Suspense>
        ) : (
          <>
            <PWAPrompt />
            <AppRoutes />
          </>
        )}
      </Router>
    </ToastProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
